import { Frame } from '~/lib';
import { ProductEventData, PromotionEvent, WishListEventData } from './models';
import { useAlgoliaEvents } from './useAlgoliaEvents';
import { addToQueue, eventStore, resetQueue, setIsReady, useIsEventsReady } from './eventStore';
import { useGTMEvents } from './useGTMEvents';
import { _transformProductsToGAItems } from './useGTMEvents/helper';
import { useLocalEvents } from './useLocalEvents';
import { useRaptorEvents } from './useRaptorEvents';
import { useCustomerStore } from '~/features/commerce-api/hooks/useCustomerStore';
import { _promotionItems, priceWithoutVAT } from './helpers';
import { useBasketStore } from '~/features/basket/hooks/useBasket';
import {
    GTMBasketItemModel,
    GTMOverlaySearchModel,
    GTMAlgoliaSearchModel,
    GTMStorePickupDrawerOpenModel,
    GTMStorePickupDrawerSelectModel,
    GTMStorePickupDrawerToggleModel,
    GTMStorePickupDrawerAccordionModel,
    GTMStorePickupDrawerQuantityModel,
    GTMStorePickupDrawerLocationSearchModel,
    GTMUsedStorePickupOptionModel,
    StorePickupOption,
} from './useGTMEvents/models';
import { GTMFilterData } from './getGtmFilterData';

type DataLayerObject = {
    event: string;
    pagePath: string;
};

declare const window: Window & {
    dataLayer: DataLayerObject[];
    td?: {
        identify?: (id: string) => void;
    };
    CookieInformation?: {
        getConsentGivenFor: (consent: 'cookie_cat_statistic') => boolean;
    };
};

export type EventIdentifier = string;

/**
 * TODO(IMC-3561): The ~48 functions returned at the bottom are rebuilt on every render,
 * re-triggering anything that lists one as a dependency. Memoise them - and note that
 * `useAlgoliaEvents` and `useRaptorEvents` must be memoised before the functions here
 * that call them can be.
 */
export function useEvents(frame: Frame | null) {
    const isPlusMember = useCustomerStore((state) => {
        return state.customer?.c_isMemberPlus ?? false;
    });

    const isReady = useIsEventsReady();

    const { aiOnInit } = useAlgoliaEvents();
    const {
        gaAuthenticationEvents,
        gaFilter,
        gaInternalSearch,
        gaProductPage,
        gtmStorePickupDrawerOpen,
        gtmStorePickupDrawerSelect,
        gtmStorePickupDrawerToggle,
        gtmStorePickupDrawerAccordion,
        gtmStorePickupDrawerQuantity,
        gtmStorePickupDrawerLocationSearch,
        gtmUsedStorePickupOption,
        gaWishlistEvents,
        gtmAddPaymentInfo,
        gtmAddShippingInfo,
        gtmAddCouponCode,
        gtmRemoveCouponCode,
        gtmAddGiftCard,
        gtmRemoveGiftCard,
        gtmAddToCart,
        gtmBeginCheckout,
        gtmInitGPage,
        gtmProductListView,
        gtmAlgoliaSearch,
        gtmPurchase,
        gtmRemoveFromCart,
        gtmSelectItem,
        gtmSelectPromotion,
        gtmViewPromotion,
        gtmViewCart,
        gtmViewItem,
        gtmReservation,
        gtmOverlaySearch,
        gtmClickWishCloudButton,
        gtmErrorWishCloud,
        gtmAddReward,
        gtmRemoveReward,
        gtmReserveVoucher,
        gtmReleaseVoucher,
        gtmErrorEvent,
        gtmUserData,
        gtmFilterTracking,
    } = useGTMEvents(frame);

    const { userChange } = useRaptorEvents(frame);

    const setLatestVisitedProduct = useLocalEvents((state) => state.setLatestVisitedProduct);

    const isTracking = () => {
        return window.CookieInformation?.getConsentGivenFor('cookie_cat_statistic');
    };

    const runQueue = () => {
        const currentQueue = eventStore.getState().queue;

        if (!currentQueue.length) return;

        console.debug('Event queue run');
        currentQueue.forEach((x) => x());
        resetQueue();
    };

    const initialize = (customerId?: string, customerNo?: string) => {
        // Prevent track current page when is just leaving it
        const allPageView = window?.dataLayer?.filter(
            (elem: { event: string }) => elem.event === 'pageview',
        );

        const lastPageView = allPageView && allPageView[allPageView.length - 1];
        if (
            isReady ||
            lastPageView?.pagePath !== document.location.pathname + document.location.search
        ) {
            console.debug('Events initialized');
            userChange(customerNo);
        }

        // Initialize
        if (isTracking()) {
            aiOnInit(customerId);
        }

        setIsReady(true);
    };

    function changeUser(customerNo?: string) {
        userChange(customerNo);
    }

    function identifyBasketIdEvent(basketId?: string) {
        console.debug('Identify event added');
        addToQueue(() => {
            console.debug('Identify event run');
            if (!basketId || !window.td || !window.td.identify) {
                return;
            }
            window.td.identify(basketId);
        });
    }

    function productsImpressionsEvent(
        itemListId: EventIdentifier,
        itemListName: EventIdentifier,
        products: ProductEventData[],
    ) {
        if (!products || !products.length) return;
        console.debug('Product impression event added', { products });
        addToQueue(() => {
            console.debug('product impression event run');
            gtmProductListView(
                _transformProductsToGAItems(products, {
                    isPlusMember,
                    withIndex: true,
                    withStockStatus: false,
                    itemListId,
                    itemListName,
                }),
            );
        });
    }

    function productClickEvent(
        itemListId: string,
        itemListName: string | undefined,
        product: ProductEventData,
        raptorModule?: string,
    ) {
        console.debug('Product click event added');
        addToQueue(() => {
            console.debug('Product click event run');
            gtmSelectItem(
                _transformProductsToGAItems([product], {
                    isPlusMember,
                    withIndex: true,
                    withStockStatus: false,
                    itemListId,
                    itemListName,
                }),
                raptorModule,
            );
        });
    }

    function promotionClickEvent(promotionDetails: PromotionEvent) {
        addToQueue(() => {
            gtmSelectPromotion(promotionDetails, _promotionItems(promotionDetails, isPlusMember));
        });
    }

    function promotionViewEvent(promotionDetails: PromotionEvent) {
        addToQueue(() => {
            gtmViewPromotion(promotionDetails, _promotionItems(promotionDetails, isPlusMember));
        });
    }

    function algoliaSearchEvent(result: GTMAlgoliaSearchModel, filters: string[]) {
        addToQueue(() => {
            console.debug('Algolia search event run');
            gtmAlgoliaSearch(result, filters);
        });
    }

    function productDetailviewEvent(product: ProductEventData, breadcrumb: string) {
        console.debug('Product detail event added');
        addToQueue(() => {
            console.debug('product detail event run');
            setLatestVisitedProduct(product.id);
            gtmViewItem(
                _transformProductsToGAItems([product], {
                    isPlusMember,
                    withIndex: false,
                    withStockStatus: true,
                }),
                breadcrumb,
            );
        });
    }

    function productReservationEvent(
        product: ProductEventData,
        storeId?: string,
        reservationNumber?: string,
    ) {
        console.debug('Product reservation');
        addToQueue(() => {
            console.debug('product reservation event run');
            gtmReservation(
                _transformProductsToGAItems([product], {
                    isPlusMember,
                    withIndex: false,
                    withStockStatus: true,
                }),
                storeId,
                reservationNumber,
            );
        });
    }

    function addToBasketEvent(
        product: ProductEventData,
        quantity = 1,
        context?: StorePickupOption.ClickAndCollect,
    ) {
        console.debug('Add to basket event added');
        const { basketId: basketID, productItems } = useBasketStore.getState().basket ?? {};

        const basketContent: GTMBasketItemModel[] =
            productItems?.map((product) => ({
                item: product?.productId || '',
                quantity: product?.quantity?.toString() || '',
                price: product?.c_unitPrice || '',
                unique_id: product?.c_ean || '',
            })) || [];

        addToQueue(() => {
            console.debug('Add to basket event run');
            gtmAddToCart({
                items: _transformProductsToGAItems([product], {
                    isPlusMember,
                    itemListName: context,
                }),
                quantity,
                basketID,
                basketContent,
                context,
            });
        });
    }

    function removeFromBasketEvent(product: ProductEventData, quantity = 1) {
        console.debug('Remove to basket event added');
        const { basketId: basketID, productItems } = useBasketStore.getState().basket ?? {};
        // const basketContent = productItems?.map((product) => product.productId).join(',');
        const basketContent: GTMBasketItemModel[] =
            productItems?.map((product) => ({
                item: product?.productId || '',
                quantity: product?.quantity?.toString() || '',
                price: product?.c_unitPrice || '',
                unique_id: product?.c_ean || '',
            })) || [];

        addToQueue(() => {
            console.debug('Remove to basket event run');
            gtmRemoveFromCart(
                _transformProductsToGAItems([product], { isPlusMember }),
                quantity,
                basketID,
                basketContent,
            );
        });
    }

    function clickWishCloudButtonEvent(product: ProductEventData, breadcrumb: string) {
        addToQueue(() => {
            console.debug('wish cloud button click event run');
            gtmClickWishCloudButton(
                _transformProductsToGAItems([product], { isPlusMember }),
                breadcrumb,
            );
        });
    }

    function viewCartEvent(products: ProductEventData[], value: string) {
        console.debug('View cart event added');
        const valueWithoutVAT = priceWithoutVAT(value)?.toString();
        addToQueue(() => {
            console.debug('View cart event run');
            gtmViewCart(
                _transformProductsToGAItems(products, { isPlusMember, withIndex: false }),
                valueWithoutVAT,
            );
        });
    }

    function beginCheckoutEvent(products: ProductEventData[], value: string, coupon: string) {
        console.debug('Begin checkout event added');
        setTimeout(() => {
            addToQueue(() => {
                console.debug('Begin checkout event run');
                const valueWithoutVAT = priceWithoutVAT(value)?.toString();
                gtmBeginCheckout(
                    _transformProductsToGAItems(products, { isPlusMember, withIndex: false }),
                    valueWithoutVAT,
                    coupon,
                );
            });
        });
    }

    function addShippingInfoEvent(
        products: ProductEventData[],
        value: string,
        coupon: string,
        shipping: string,
    ) {
        console.debug('Add shipping info event added');
        addToQueue(() => {
            console.debug('Add shipping info event run');
            const valueWithoutVAT = priceWithoutVAT(value)?.toString();
            gtmAddShippingInfo(
                _transformProductsToGAItems(products, { isPlusMember, withIndex: false }),
                valueWithoutVAT,
                coupon,
                shipping,
            );
        });
    }

    function addPaymentInfoEvent(
        products: ProductEventData[],
        value: string,
        coupon: string,
        payment: string,
    ) {
        setTimeout(() => {
            addToQueue(() => {
                const valueWithoutVAT = priceWithoutVAT(value)?.toString();
                gtmAddPaymentInfo(
                    _transformProductsToGAItems(products, { isPlusMember, withIndex: false }),
                    valueWithoutVAT,
                    coupon,
                    payment,
                );
            });
        });
    }

    function addCouponCodeEvent(products: ProductEventData[], value: string, coupon: string) {
        console.debug('Add coupon info event added');
        const valueWithoutVAT = priceWithoutVAT(value)?.toString();
        addToQueue(() => {
            console.debug('Add coupon info event run');
            gtmAddCouponCode(
                _transformProductsToGAItems(products, { isPlusMember }),
                valueWithoutVAT,
                coupon,
            );
        });
    }

    function removeCouponCodeEvent(products: ProductEventData[], value: string, coupon: string) {
        console.debug('Remove coupon info event added');
        const valueWithoutVAT = priceWithoutVAT(value)?.toString();
        addToQueue(() => {
            console.debug('Remove coupon info event run');
            gtmRemoveCouponCode(
                _transformProductsToGAItems(products, { isPlusMember }),
                valueWithoutVAT,
                coupon,
            );
        });
    }

    // these are currently not used?
    function addGiftCardEvent(products: ProductEventData[], value: string, coupon: string) {
        console.debug('Add gift card event added');
        const valueWithoutVAT = priceWithoutVAT(value)?.toString();
        addToQueue(() => {
            console.debug('Add gift card event run');
            gtmAddGiftCard(
                _transformProductsToGAItems(products, { isPlusMember }),
                valueWithoutVAT,
                coupon,
            );
        });
    }
    // these are currently not used?
    function removeGiftCardEvent(products: ProductEventData[], value: string, coupon: string) {
        console.debug('Remove gift card event added');
        const valueWithoutVAT = priceWithoutVAT(value)?.toString();
        addToQueue(() => {
            console.debug('Remove gift card event run');
            gtmRemoveGiftCard(
                _transformProductsToGAItems(products, { isPlusMember }),
                valueWithoutVAT,
                coupon,
            );
        });
    }

    function addRewardEvent(rewardCode: string) {
        console.debug('Add reward event added');
        addToQueue(() => {
            console.debug('Add reward event run');
            gtmAddReward(rewardCode);
        });
    }

    function removeRewardEvent(rewardCode: string) {
        console.debug('Remove reward event added');
        addToQueue(() => {
            console.debug('Remove reward event run');
            gtmRemoveReward(rewardCode);
        });
    }

    function reserveVoucherEvent(promoId: string) {
        console.debug('Reserve voucher event added');
        addToQueue(() => {
            console.debug('Reserve voucher event run');
            gtmReserveVoucher(promoId);
        });
    }

    function releaseVoucherEvent(promoId: string) {
        console.debug('Release voucher event added');
        addToQueue(() => {
            console.debug('Release voucher event run');
            gtmReleaseVoucher(promoId);
        });
    }

    function purchaseEvent(
        affiliationOptions: {
            storeId: string | undefined;
            storeName: string | undefined;
            isLoggedIn: boolean | undefined;
            isStorePersonnel: boolean | undefined;
            paymentMethod: string | undefined;
        },
        coupon: string,
        transaction_id: string,
        items: ProductEventData[],
        value: number,
        shipping: number,
    ) {
        const valueWithoutVAT = priceWithoutVAT(value);

        addToQueue(() => {
            gtmPurchase(
                affiliationOptions,
                coupon,
                transaction_id,
                _transformProductsToGAItems(items, { isPlusMember, withIndex: false }),
                valueWithoutVAT,
                shipping,
            );
        });
    }

    function overlaySearchResultEvent(params: GTMOverlaySearchModel) {
        addToQueue(() => {
            console.debug('overlay search result event run');
            gtmOverlaySearch(params);
        });
    }

    function overlaySearchClickEvent(params: GTMOverlaySearchModel) {
        addToQueue(() => {
            console.debug('overlay search click event run');
            gtmOverlaySearch(params);
        });
    }

    /**
     * Custom GA Events
     */
    function filterEvent(eventAction: string, eventLabel: string) {
        console.debug(`Filter ${eventAction} event added`);
        gaFilter(eventAction, eventLabel);
    }

    function PDPActionEvent(eventAction: string, eventLabel: string) {
        console.debug('PDP action event added');
        gaProductPage(eventAction, eventLabel);
    }

    function storePickupDrawerOpenEvent(params: GTMStorePickupDrawerOpenModel) {
        console.debug('Store pickup drawer open event added');
        gtmStorePickupDrawerOpen(params);
    }

    function storePickupDrawerSelectEvent(params: GTMStorePickupDrawerSelectModel) {
        console.debug('Store pickup drawer select event added');
        gtmStorePickupDrawerSelect(params);
    }

    function storePickupDrawerToggleEvent(params: GTMStorePickupDrawerToggleModel) {
        console.debug('Store pickup drawer toggle event added');
        gtmStorePickupDrawerToggle(params);
    }

    function storePickupDrawerAccordionEvent(params: GTMStorePickupDrawerAccordionModel) {
        console.debug('Store pickup drawer accordion event added');
        gtmStorePickupDrawerAccordion(params);
    }

    function storePickupDrawerQuantityEvent(params: GTMStorePickupDrawerQuantityModel) {
        console.debug('Store pickup drawer quantity event added');
        gtmStorePickupDrawerQuantity(params);
    }

    function storePickupDrawerLocationSearchEvent(params: GTMStorePickupDrawerLocationSearchModel) {
        console.debug('Store pickup drawer location search event added');
        gtmStorePickupDrawerLocationSearch(params);
    }

    function usedStorePickupOptionEvent(params: GTMUsedStorePickupOptionModel) {
        console.debug('Used store pickup option event added');
        addToQueue(() => {
            console.debug('used store pickup option event run');
            gtmUsedStorePickupOption(params);
        });
    }

    function internalSearchEvent(eventLabel: string) {
        console.debug('Internal Search event added');
        gaInternalSearch(eventLabel);
    }

    function authenticationEvent(eventAction: string, eventLabel: string | undefined) {
        console.debug('Authentication event added');
        gaAuthenticationEvents(eventAction, eventLabel);
    }

    function wishlistEvent(wishListEventData: WishListEventData) {
        console.debug('Wishlist event added');
        gaWishlistEvents(wishListEventData);
    }

    function errorEvent(err: string, innerError: Error) {
        addToQueue(() => {
            console.debug('error event run');
            gtmErrorEvent(err, innerError);
        });
    }

    function filterTrackingEvent(filterEventData: GTMFilterData) {
        console.debug('Filter event added');
        gtmFilterTracking(filterEventData);
    }

    return {
        isReady,
        initialize,
        pageView: gtmInitGPage,
        userData: gtmUserData,
        filterTrackingEvent,
        productsImpressionsEvent,
        productClickEvent,
        promotionClickEvent,
        promotionViewEvent,
        algoliaSearchEvent,
        productDetailviewEvent,
        productReservationEvent,
        addToBasketEvent,
        removeFromBasketEvent,
        beginCheckoutEvent,
        addShippingInfoEvent,
        addPaymentInfoEvent,
        addCouponCodeEvent,
        removeCouponCodeEvent,
        addGiftCardEvent,
        removeGiftCardEvent,
        viewCartEvent,
        purchaseEvent,
        filterEvent,
        PDPActionEvent,
        storePickupDrawerOpenEvent,
        storePickupDrawerSelectEvent,
        storePickupDrawerToggleEvent,
        storePickupDrawerAccordionEvent,
        storePickupDrawerQuantityEvent,
        storePickupDrawerLocationSearchEvent,
        usedStorePickupOptionEvent,
        internalSearchEvent,
        authenticationEvent,
        wishlistEvent,
        runQueue,
        setIsReady,
        identifyBasketIdEvent,
        changeUser,
        overlaySearchResultEvent,
        overlaySearchClickEvent,
        clickWishCloudButtonEvent,
        addRewardEvent,
        removeRewardEvent,
        reserveVoucherEvent,
        releaseVoucherEvent,
        errorEvent,
        errorEventWishCloud: () => {
            addToQueue(() => {
                console.debug('wish cloud error event run');
                gtmErrorWishCloud();
            });
        },
    };
}
